import React, { useEffect, useState } from 'react';
import { useTenantContext } from '../../context/TenantContext';
import ColorThemeConfig from './ColorThemeConfig';
import AdminHeaderConfig from './AdminHeaderConfig';
import PublicHeaderConfig from './PublicHeaderConfig';
import PublicFooterConfig from './PublicFooterConfig';

interface TenantColorsAndFeaturesProps {
  formData: any;
  handleConfigChange: (path: string, value: any) => void;
}

// (désormais géré par des sous-composants: AdminHeaderConfig, PublicHeaderConfig, ColorThemeConfig)

export const TenantColorsAndFeatures: React.FC<TenantColorsAndFeaturesProps> = ({
  formData,
  handleConfigChange,
}) => {
  const [activeTab, setActiveTab] = useState<'colors' | 'header' | 'public' | 'footer'>(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const sub = params.get('subtab');
      if (sub === 'colors' || sub === 'header' || sub === 'public' || sub === 'footer') return sub as any;
      const saved = localStorage.getItem('tenant-admin-config-active-tab:global');
      return (saved === 'colors' || saved === 'header' || saved === 'public' || saved === 'footer') ? (saved as any) : 'colors';
    } catch {
      return 'colors';
    }
  });
  const { clearPreview, tenant } = useTenantContext();
  const storageKey = React.useMemo(() => `tenant-admin-config-active-tab:${tenant?.id ?? 'global'}`, [tenant?.id]);


  // Charger l'onglet actif sauvegardé après un rechargement (ne pas écraser si ?subtab= est présent)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const sub = params.get('subtab');
      if (sub === 'colors' || sub === 'header' || sub === 'public' || sub === 'footer') return; // priorité à l'URL
      const saved = localStorage.getItem(storageKey);
      if (saved === 'colors' || saved === 'header' || saved === 'public' || saved === 'footer') {
        setActiveTab(saved as any);
      }
    } catch {}
  }, [storageKey]);

  // Persister l'onglet actif à chaque changement (clé par tenant + clé globale) et synchroniser l'URL ?subtab=
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, activeTab);
      localStorage.setItem('tenant-admin-config-active-tab:global', activeTab);
      const url = new URL(window.location.href);
      url.searchParams.set('subtab', activeTab);
      window.history.replaceState({}, '', url.toString());
    } catch {}
  }, [activeTab, storageKey]);

  // Nettoyer l'aperçu quand on quitte la page
  useEffect(() => {
    return () => {
      clearPreview();
    };
  }, [clearPreview]);

  return (
    <section className="space-y-8">
      {/* Onglets */}
      <div className="flex space-x-1 bg-white/5 p-1 rounded-lg">
        <button
          onClick={() => setActiveTab('colors')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'colors' 
              ? 'bg-white/10 text-white' 
              : 'text-gray-400 hover:text-white hover:bg-white/5'
          }`}
        >
          Couleurs du thème
        </button>
        <button
          onClick={() => setActiveTab('header')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'header' 
              ? 'bg-white/10 text-white' 
              : 'text-gray-400 hover:text-white hover:bg-white/5'
          }`}
        >
          Header Admin
        </button>
        <button
          onClick={() => setActiveTab('public')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'public' 
              ? 'bg-white/10 text-white' 
              : 'text-gray-400 hover:text-white hover:bg-white/5'
          }`}
        >
          Header Public
        </button>
        <button
          onClick={() => setActiveTab('footer')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'footer' 
              ? 'bg-white/10 text-white' 
              : 'text-gray-400 hover:text-white hover:bg-white/5'
          }`}
        >
          Footer Public
        </button>
      </div>

      {/* Contenu des onglets */}
      {activeTab === 'colors' && (
        <ColorThemeConfig formData={formData} handleConfigChange={handleConfigChange} />
      )}

      {activeTab === 'header' && (
        <AdminHeaderConfig formData={formData} handleConfigChange={handleConfigChange} />
      )}

      {activeTab === 'public' && (
        <PublicHeaderConfig formData={formData} handleConfigChange={handleConfigChange} />
      )}

      {activeTab === 'footer' && (
        <PublicFooterConfig formData={formData} handleConfigChange={handleConfigChange} />
      )}
    </section>
  );
};
